import React, { useState, useRef, useEffect } from 'react';
import CounterPost from './CounterPost';
import { Post } from '../../types/Post';
import { getMonsterImgPath } from '../../utils/monster';

import ProfileWithName from '../../components/ProfileWithName';
import { isBattleRecord, monthDayTime } from '../../utils/date';
import CustomDropdown from '../../components/CustomDropdown';
import SeasonSelect from '../Common/SeasonSelect';
import GroupSelect from '../Common/GroupSelect';
import MatchSummary from '../Common/MatchSummary';
import SiegeMapTimeline, { useSiegeMapAvailable } from '../Common/SiegeMapTimeline';
import { readGroups, fetchGroups } from '../../utils/groupCache';
import Caret from '../../components/Caret';
import { IconChart, IconComment } from '../../components/Icons';
import BackButton from '../../components/BackButton';
import { guildImg } from '../../utils/guildImage';
import GradeBadge from '../../components/GradeBadge';

export interface PostListProps {
  posts: any[];
  allPosts?: any[]; // 시즌 전체 게시물 (그룹 통계 계산용)
  filteredPosts?: any[]; // 현재 필터 적용된 게시물
  /** 길드원 탭 통계용 — 공격/방어·승패·검색과 무관하게 그 그룹의 공격 기록 전부(점령전 전투기록). 없으면 filteredPosts */
  memberPosts?: any[];
  monsterMapping: any;
  user: any;
  searchValue: string;
  setSearchValue: (v: string) => void;
  onSearch: () => void;
  onSearchReset?: () => void;
  onPostClick: (post: any) => void;
  selectedPost: any;
  setSelectedPost: (post: any) => void;
  expandedPosts: Set<string>;
  togglePost: (postId: string) => void;
  onUploadClick: () => void;
  onPostDeleted?: () => void;
  selectedSeason: number;
  setSelectedSeason: (season: number) => void;
  selectedSiegeWar?: string;
  setSelectedSiegeWar?: (siegeWar: string) => void;
  selectedResult?: string;
  setSelectedResult?: (result: string) => void;
  /** 상대 길드 고르기(사용자 2026-10-31) — 'all' 또는 길드 이름. oppGuilds 는 고른 매치업의 상대 길드들 */
  selectedOpp?: string;
  setSelectedOpp?: (guild: string) => void;
  oppGuilds?: string[];
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  battleStep?: 'season' | 'group' | 'list';
  onSeasonSelect?: (season: number) => void;
  onGroupSelect?: (group: string) => void;
  onBackToSeason?: () => void;
  onBackToGroup?: () => void;
  totalCount?: number;
  prefetchedGroups?: any[];
  /** 그룹 카드에 찍을 승/패. SiegeBattleList 가 서버 집계값으로 채워 넘긴다.
      아래 로컬 state groupStats(고른 그룹 하나의 집계)와 다른 값이라 이름을 달리 둔다 */
  groupWinLoss?: { [groupName: string]: { wins: number; losses: number; winRate?: number; defenseRate?: number } };
  /** 내 길드명. 배너에 표시된다. 기본값은 기존 동작 유지용 */
  myGuildName?: string;
  /** 시즌 선택 화면에 띄울 목록. 없으면 SeasonSelect 기본값을 쓴다 */
  seasonList?: number[];
  /** 시즌 카드 문구. 점령전 회차처럼 숫자가 길 때 쓴다 */
  formatSeasonLabel?: (season: number) => string;
  /** 공덱/방덱 전환. 넘기지 않으면 표시하지 않는다 */
  logType?: number;
  setLogType?: (v: number) => void;
  /** 기록을 마지막으로 받아온 시각 (ISO). 없으면 아무것도 안 그린다 */
  syncedAt?: string | null;
}

/**
 * **마지막으로 받아온 시각.**
 *
 * 이 화면의 값이 언제 것인지 알 수 있어야 한다 — 수집이 멈춰 있어도 화면은 멀쩡해
 * 보이기 때문이다(실제로 프록시가 끊겨 이틀치가 안 들어온 적이 있다).
 */
const SyncedAt = ({ at, compact = false }: { at?: string | null; compact?: boolean }) => {
  if (!at) return null;
  return (
    <span style={{
      fontSize: compact ? 10 : 12,
      color: '#868e96',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
      마지막 갱신 {monthDayTime(at, false)}
    </span>
  );
};

const PostList: React.FC<PostListProps> = ({
  posts: initialPosts,
  allPosts,
  filteredPosts,
  memberPosts,
  monsterMapping,
  user,
  searchValue,
  setSearchValue,
  onSearch,
  onSearchReset,
  onPostClick,
  selectedPost,
  setSelectedPost,
  expandedPosts,
  togglePost,
  onUploadClick,
  onPostDeleted,
  selectedSeason,
  setSelectedSeason,
  selectedSiegeWar = 'all',
  setSelectedSiegeWar = () => {},
  selectedResult = 'all',
  setSelectedResult = () => {},
  selectedOpp = 'all',
  setSelectedOpp,
  oppGuilds = [],
  onLoadMore,
  hasMore,
  isLoading,
  battleStep = 'list',
  onSeasonSelect,
  onGroupSelect,
  onBackToSeason,
  onBackToGroup,
  totalCount = 0,
  prefetchedGroups,
  groupWinLoss,
  myGuildName = '설탕',
  seasonList,
  formatSeasonLabel,
  logType,
  setLogType,
  syncedAt
}) => {
  // 길드원 탭에서 사용할 게시물 (현재 그룹 필터 적용된 데이터)
  const postsForMemberStats = memberPosts || filteredPosts || initialPosts;
  const observerTarget = useRef<HTMLDivElement>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadedGroupsSeason, setLoadedGroupsSeason] = useState<number | null>(null); // 이미 로드된 시즌 추적
  const [selectedGroupData, setSelectedGroupData] = useState<any>(null);
  const [groupStats, setGroupStats] = useState<{ wins: number; losses: number }>({ wins: 0, losses: 0 });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [listTab, setListTab] = useState<'members' | 'records' | 'map'>('members');
  /** [거점 현황] 탭 — 그 점령전의 거점 지도 기록이 있을 때만(사용자 2026-10-13) */
  const hasMap = useSiegeMapAvailable(selectedGroupData?.siegeId);
  useEffect(() => { if (!hasMap && listTab === 'map') setListTab('members'); }, [hasMap, listTab]);
  const [expandedMemberKey, setExpandedMemberKey] = useState<string | null>(null);

  // 시즌 목록 가져오기
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch('/api/posts/seasons');
        if (response.ok) {
          const data = await response.json();
          setSeasons(data.seasons || []);
          // 시즌이 선택되지 않았고 시즌 목록이 있으면 첫 번째 시즌 선택
          if (data.seasons.length > 0 && !selectedSeason) {
            setSelectedSeason(data.seasons[0]);
          }
        }
      } catch (error) {
      }
    };
    fetchSeasons();
  }, []);

  // 그룹 목록 가져오기 (프리페치 데이터 우선 사용)
  useEffect(() => {
    if (!selectedSeason || selectedSeason === 0) return;
    if (loadedGroupsSeason === selectedSeason) return;

    // 프리페치된 데이터가 있으면 바로 사용 (네트워크 요청 없음)
    if (prefetchedGroups && prefetchedGroups.length > 0) {
      setGroups(prefetchedGroups);
      setLoadedGroupsSeason(selectedSeason);
      return;
    }

    /**
     * 저장된 카드가 있으면 **먼저 그린다.**
     *
     * 시즌을 고르면 그룹 카드가 뜨기까지 빈 화면이 보였다 — 시즌 목록은 세션 저장소에
     * 남겨 두는데 그 다음 화면인 카드는 매번 새로 받았기 때문이다. 지난번 목록으로 바로
     * 그리고, 아래에서 새로 받아 갱신한다.
     */
    const cached = readGroups(selectedSeason);
    if (cached) setGroups(cached);

    const load = async () => {
      try {
        setGroups(await fetchGroups(selectedSeason));
        setLoadedGroupsSeason(selectedSeason);
      } catch (error) {
        // 저장된 값이 있으면 그걸로 버틴다
        if (cached) setLoadedGroupsSeason(selectedSeason);
      }
    };
    load();
  }, [selectedSeason, loadedGroupsSeason, prefetchedGroups]);

  // 선택된 그룹의 상세 정보 가져오기 (이미 로드된 groups에서 찾기)
  useEffect(() => {
    if (selectedSiegeWar && selectedSiegeWar !== 'all' && groups.length > 0) {
      const group = groups.find((g: any) => g.groupName === selectedSiegeWar);
      setSelectedGroupData(group || null);
    } else {
      setSelectedGroupData(null);
    }
  }, [selectedSiegeWar, groups]);

  // 선택된 그룹의 전체 통계 계산 (이미 로드된 allPosts에서 계산, 추가 fetch 없음)
  useEffect(() => {
    setGroupStats({ wins: 0, losses: 0 });

    if (!selectedSeason || !allPosts || allPosts.length === 0) {
      setIsLoadingStats(false);
      return;
    }

    // '전체' 는 그룹으로 거르지 않는다 — 예전에는 여기서 그냥 빠져나가 승/패가 안 나왔다
    let filtered = selectedSiegeWar === 'all'
      ? allPosts
      : allPosts.filter((post: any) => post.group === selectedSiegeWar);

    if (selectedResult === 'my' && user?.nickname) {
      filtered = filtered.filter((post: any) => post.nickname === user.nickname);
    }

    const wins = filtered.filter((post: any) => post.result === 'win').length;
    const losses = filtered.filter((post: any) => post.result === 'lose').length;
    setGroupStats({ wins, losses });
    setIsLoadingStats(false);
  }, [allPosts, selectedSiegeWar, selectedResult, user?.nickname]);

  // Intersection Observer로 스크롤 끝 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        threshold: 0.5,
        rootMargin: '1000px' // 화면 아래 1000px 전에 미리 로딩
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);
  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>

      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px'
      }}>
        {/* 헤더 섹션 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '40px 32px 32px',
          marginBottom: 32,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          position: 'relative',
          zIndex: 100,
          overflow: 'visible'
        }}>
          {/* 전투기록 제목 - list가 아닐 때만 표시 */}
          {battleStep !== 'list' && (
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <h1 style={{
                color: '#333',
                fontSize: 40,
                fontWeight: 900,
                margin: 0,
                marginBottom: 12,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-1px'
              }}>전투기록</h1>
              <p style={{
                color: '#999',
                fontSize: 14,
                margin: 0,
                fontWeight: 500
              }}>점령전 중 일정한 주기로 자동으로 업데이트됩니다</p>
            </div>
          )}

          {/* 전투기록 리스트 헤더 - list 단계에서만 표시 */}
          {battleStep === 'list' && (
            <>
              {/* 머리 줄 — 왼쪽 뒤로 가기, 오른쪽 마지막 갱신. 그 아래 매치업 (사용자 시안) */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
                <BackButton onClick={onBackToGroup} />
                {syncedAt && <SyncedAt at={syncedAt} />}
              </div>

              {/* 매치업 — 점령전 한 판을 골랐고 게임 숫자(매치업 현황)가 있으면 길드를 골라 보는 판 하나로, 아니면 예전 배너 + 승/패 상자 */}
              {(() => {
                // 길드 매치업 배너
                const banner = (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
                borderRadius: 16,
                padding: '16px 28px',
                gap: 20,
                boxShadow: '0 2px 10px rgba(102,126,234,0.1)',
                border: '1px solid rgba(102,126,234,0.15)',
              }}>
                {/* 내 길드 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <img loading="lazy"
                    src={guildImg(myGuildName)}
                    alt={myGuildName}
                    style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(102,126,234,0.3)' }}
                    onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5', textAlign: 'center', whiteSpace: 'nowrap' }}>{myGuildName}</span>
                </div>

                {/* 상대 길드가 있을 때만 VS 를 그린다 — '전체' 에서는 "설탕 VS" 만 남았다 */}
                {selectedGroupData?.opponentGuilds?.length > 0 && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#667eea', letterSpacing: '2px', flexShrink: 0 }}>VS</span>
                )}
                {/* 상대 길드들 */}
                {selectedGroupData?.opponentGuilds?.length > 0 ? (
                  selectedGroupData.opponentGuilds.map((guild: string, i: number) => (
                    <React.Fragment key={guild}>
                      {i > 0 && (
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#667eea', letterSpacing: '2px', flexShrink: 0 }}>VS</span>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <img loading="lazy"
                          src={guildImg(guild)}
                          alt={guild}
                          style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(0,0,0,0.1)' }}
                          onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', textAlign: 'center', whiteSpace: 'nowrap' }}>{guild}</span>
                      </div>
                    </React.Fragment>
                  ))
                ) : null}
              </div>
                );
                const oldBoxes = (() => {
                const wins = groupStats.wins;
                const losses = groupStats.losses;
                const total = wins + losses;
                const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

                return (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16,
                    marginBottom: 24
                  }}>
                    {/* 승리 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%)',
                      borderRadius: 10,
                      padding: '8px 16px',
                      border: '2px solid rgba(33, 150, 243, 0.3)',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      minWidth: 80
                    }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#1976d2',
                        letterSpacing: '0.3px'
                      }}>승리</span>
                      <span style={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: '#2196F3',
                        textShadow: '0 1px 3px rgba(33, 150, 243, 0.2)'
                      }}>{wins}</span>
                    </div>

                    {/* 패배 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%)',
                      borderRadius: 10,
                      padding: '8px 16px',
                      border: '2px solid rgba(244, 67, 54, 0.3)',
                      boxShadow: '0 2px 8px rgba(244, 67, 54, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      minWidth: 80
                    }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#d32f2f',
                        letterSpacing: '0.3px'
                      }}>패배</span>
                      <span style={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: '#f44336',
                        textShadow: '0 1px 3px rgba(244, 67, 54, 0.2)'
                      }}>{losses}</span>
                    </div>

                    {/* 승률 박스 */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
                      borderRadius: 10,
                      padding: '8px 16px',
                      border: '2px solid rgba(76, 175, 80, 0.3)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      minWidth: 80
                    }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#388e3c',
                        letterSpacing: '0.3px'
                      }}>승률</span>
                      <span style={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: '#4caf50',
                        textShadow: '0 1px 3px rgba(76, 175, 80, 0.2)'
                      }}>{winRate}%</span>
                    </div>
                  </div>
                );
                })();
                const old = <>{banner}{oldBoxes}</>;
                return selectedGroupData?.siegeId
                  ? <MatchSummary siegeId={selectedGroupData.siegeId} fallback={old} compact={false} />
                  : old;
              })()}
            </>
          )}
        </div>

        {/* 시즌 선택 카드 영역 */}
        {battleStep === 'season' && onSeasonSelect && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '32px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          minHeight: 400,
          position: 'relative',
          zIndex: 1
        }}>
          <SeasonSelect onSelect={onSeasonSelect} seasons={seasonList} formatLabel={formatSeasonLabel} />
        </div>
        )}

        {/* 그룹 선택 카드 영역 */}
        {battleStep === 'group' && onGroupSelect && onBackToSeason && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '32px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          minHeight: 400,
          position: 'relative',
          zIndex: 1
        }}>
          <GroupSelect
            season={selectedSeason}
            onSelect={onGroupSelect}
            onBack={onBackToSeason}
            groupStats={groupWinLoss}
            groups={groups}
          />
        </div>
        )}

        {/* 게시글 목록 */}
        {battleStep === 'list' && (
        <>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '32px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          minHeight: 400,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isLoading && initialPosts.length === 0 ? 'center' : 'flex-start'
        }}>
          {/* 탭 */}
          <div style={{
            display: 'flex',
            width: '100%',
            marginBottom: 24,
            borderBottom: '2px solid #eee'
          }}>
            <button
              onClick={() => setListTab('members')}
              data-subtab=""
              style={{
                flex: 1,
                padding: '14px 0',
                border: 'none',
                background: listTab === 'members' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'members' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: '12px 12px 0 0'
              }}
            >
              길드원
            </button>
            <button
              onClick={() => setListTab('records')}
              data-subtab=""
              style={{
                flex: 1,
                padding: '14px 0',
                border: 'none',
                background: listTab === 'records' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'records' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: '12px 12px 0 0'
              }}
            >
              전투기록
            </button>
            {hasMap && (
            <button
              onClick={() => setListTab('map')}
              data-subtab=""
              style={{
                flex: 1,
                padding: '14px 0',
                border: 'none',
                background: listTab === 'map' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: listTab === 'map' ? '#fff' : '#666',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: '12px 12px 0 0'
              }}
            >
              거점 현황
            </button>
            )}
          </div>

          {/* 공격/방어·승패 고르기는 전투기록 탭 안에 둔다(사용자) — 머리 줄에는 뒤로 가기만 */}
          {listTab === 'records' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* 상대 길드 — 공격 왼쪽(사용자 2026-10-31). 상대가 둘 이상일 때만 */}
                  {setSelectedOpp && oppGuilds.length > 1 && (
                    <CustomDropdown
                      value={selectedOpp}
                      onChange={(val) => setSelectedOpp(String(val))}
                      options={[{ value: 'all', label: '상대 전체' }, ...oppGuilds.map((g) => ({ value: g, label: g }))]}
                      icon="🏰"
                      style={{ minWidth: 150 }}
                    />
                  )}

                  {/* 공덱/방덱 전환 */}
                  {logType !== undefined && setLogType && (
                    <CustomDropdown
                      value={logType}
                      onChange={(val) => setLogType(Number(val))}
                      options={[
                        { value: 1, label: '공격' },
                        { value: 2, label: '방어' }
                      ]}
                      icon="⚔️"
                      style={{ minWidth: 120 }}
                    />
                  )}

                  {/* 승패 필터 */}
                  <CustomDropdown
                    value={selectedResult}
                    onChange={(val) => setSelectedResult(val)}
                    options={[
                      { value: 'all', label: '전체' },
                      { value: 'my', label: '내 기록' },
                      { value: 'win', label: '승리' },
                      { value: 'lose', label: '패배' }
                    ]}
                    icon={<IconChart />}
                    style={{ minWidth: 160 }}
                  />
                </div>
            </div>
          )}

          <div style={{ width: '100%' }}>
          {/* 게시글 리스트 */}
          {isLoading && initialPosts.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                color: '#667eea',
                fontSize: 16,
                fontWeight: 600
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  border: '4px solid rgba(102, 126, 234, 0.2)',
                  borderTopColor: '#667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span>전투기록을 불러오는 중...</span>
              </div>
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : listTab === 'map' && selectedGroupData?.siegeId ? (
            <SiegeMapTimeline siegeId={selectedGroupData.siegeId} compact={false} />
          ) : listTab === 'members' ? (
            /* 길드원 탭 */
            (() => {
              // 길드원별 승/패 집계 (전체 게시물 사용)
              const memberStats = new Map<string, { nickname: string; profileImg: string; oddscore: number; wins: number; losses: number; records: any[]; key: string; isDeleted?: boolean }>();
              postsForMemberStats.forEach((post: any) => {
                const key = post.author || post.nickname;
                if (!memberStats.has(key)) {
                  // 길드를 나간 사람이면 이름을 연한 빨강으로 보여 준다
                  memberStats.set(key, { nickname: post.nickname || '알수없음', profileImg: post.profileImg, oddscore: post.oddscore || 0, wins: 0, losses: 0, records: [], key, isDeleted: post.isDeleted });
                }
                const stat = memberStats.get(key)!;
                if (post.result === 'win') stat.wins++;
                else if (post.result === 'lose') stat.losses++;
                stat.records.push(post);
              });

              // 정렬: 승률 높은 순, 같으면 전투 수 많은 순
              const sortedMembers = Array.from(memberStats.values()).sort((a, b) => {
                const aWinRate = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
                const bWinRate = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
                if (bWinRate !== aWinRate) return bWinRate - aWinRate;
                return (b.wins + b.losses) - (a.wins + a.losses);
              });

              return (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {sortedMembers.map((member, idx) => {
                    const total = member.wins + member.losses;
                    const winRate = total > 0 ? ((member.wins / total) * 100).toFixed(1) : '0.0';
                    const isExpanded = expandedMemberKey === member.key;
                    return (
                      <li
                        key={idx}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 16,
                          border: isExpanded ? '2px solid rgba(102, 126, 234, 0.3)' : '2px solid rgba(102, 126, 234, 0.1)',
                          boxShadow: isExpanded ? '0 8px 24px rgba(102, 126, 234, 0.15)' : '0 4px 12px rgba(102, 126, 234, 0.08)',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* 길드원 헤더 */}
                        <div
                          style={{
                            padding: '16px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                          onClick={() => {
                            // 스크롤 위치 저장
                            const scrollY = window.scrollY;
                            setExpandedMemberKey(isExpanded ? null : member.key);
                            // 다음 렌더링 후 스크롤 위치 복원
                            requestAnimationFrame(() => {
                              window.scrollTo(0, scrollY);
                            });
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.04)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {/* 프로필 */}
                          <img loading="lazy"
                            src={
                              member.profileImg || '/images/default-profile.png'
                            }
                            alt="프로필"
                            style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(102, 126, 234, 0.2)' }}
                            onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                          />
                          {/* 닉네임 */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 16, color: member.isDeleted ? '#ff6b6b' : '#333', marginBottom: 4 }}>{member.nickname}</div>
                            <div style={{ fontSize: 13, color: '#888' }}>전투 {total}회</div>
                          </div>
                          {/* 승패 통계 */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            background: 'rgba(0, 0, 0, 0.03)',
                            padding: '10px 16px',
                            borderRadius: 12
                          }}>
                            <span style={{ fontSize: 15, color: '#5a7a9b', fontWeight: 700 }}>{member.wins}승</span>
                            <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                            <span style={{ fontSize: 15, color: '#9b5a6f', fontWeight: 700 }}>{member.losses}패</span>
                            <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                            <span style={{ fontSize: 15, color: '#8b7355', fontWeight: 700 }}>{winRate}%</span>
                          </div>
                          {/* 펼침 아이콘 */}
                          <div style={{
                            fontSize: 16,
                            color: '#667eea',
                            fontWeight: 700,
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}>
                            <Caret />
                          </div>
                        </div>

                        {/* 전투기록 드롭다운 */}
                        {isExpanded && (
                          <div style={{
                            borderTop: '1px solid rgba(102, 126, 234, 0.1)',
                            background: 'rgba(102, 126, 234, 0.02)',
                            padding: '16px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10
                          }}>
                            {member.records.map((post: any, recordIdx: number) => {
                              const isWin = post.result === 'win';
                              const isLose = post.result === 'lose';
                              return (
                                <div
                                  key={post._id || recordIdx}
                                  style={{
                                    background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: 12,
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.15)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateX(4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateX(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    let newViews = post.views || 0;
                                    try {
                                      const res = await fetch('/api/posts/view', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ id: post._id })
                                      });
                                      const data = await res.json();
                                      if (data && typeof data.views === 'number') {
                                        newViews = data.views;
                                      }
                                    } catch {}
                                    setSelectedPost({ ...post, views: newViews });
                                  }}
                                >
                                  {/* 결과 배지 */}
                                  <div style={{
                                    background: isWin ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' : isLose ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : '#999',
                                    color: '#fff',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: '5px 12px',
                                    borderRadius: 8,
                                    minWidth: 36,
                                    textAlign: 'center'
                                  }}>
                                    {isWin ? '승' : isLose ? '패' : '-'}
                                  </div>
                                  {/* 구분선 */}
                                  <div style={{ width: 1, height: 20, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                                  {/* 공덱 몬스터 */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {post.attackTeam?.map((m: any, mIdx: number) => {
                                      const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                      return (
                                        <img loading="lazy"
                                          key={mIdx}
                                          src={imgPath ? `/mobs/v2/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png'}
                                          alt={displayName}
                                          style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102, 126, 234, 0.15)' }}
                                        />
                                      );
                                    })}
                                  </div>
                                  {/* 한쪽 덱이 없으면 VS 를 그리지 않는다 — 방어 기록에는 상대 공덱이 없다 */}
                                  {post.attackTeam?.length > 0 && post.defenseTeam?.length > 0 && (
                                    <span style={{ color: '#667eea', fontSize: 14, fontWeight: 600 }}>VS</span>
                                  )}
                                  {/* 방덱 몬스터 */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {post.defenseTeam?.map((m: any, mIdx: number) => {
                                      const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                      return (
                                        <img loading="lazy"
                                          key={mIdx}
                                          src={imgPath ? `/mobs/v2/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png'}
                                          alt={displayName}
                                          style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102, 126, 234, 0.15)' }}
                                        />
                                      );
                                    })}
                                  </div>
                                  {/* 구분선 */}
                                  <div style={{ width: 1, height: 20, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                                  {/* 상대 + 전투 시각
                                      시각은 상대 이름 아래에 쌓는다 — 칸을 따로 두지 않아도 되고,
                                      예전에는 오른쪽 끝에 **그룹 시작일**(`selectedGroupData.startDate`)을
                                      찍고 있어서 모든 줄이 같은 날짜였다. 전투마다 다른 값이 맞다. */}
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 'auto', lineHeight: 1.25 }}>
                                    {/* 엠블럼은 이름과 **같은 줄**에 둔다. 시각까지 한 묶음으로
                                        가운데 맞추면 엠블럼이 두 줄 사이에 걸려 어정쩡해진다 */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      {post.siegeWar && (
                                        <img loading="lazy"
                                          src={guildImg(post.siegeWarImg || post.siegeWar)}
                                          alt={post.siegeWar}
                                          style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)' }}
                                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                      )}
                                      <span style={{ fontWeight: 600, fontSize: 13, color: '#667eea' }}>{post.siegeWar || '-'}</span>
                                    </div>
                                    {post.createdAt && (
                                      <span style={{ fontSize: 11, fontWeight: 400, color: '#999', whiteSpace: 'nowrap' }}>
                                        {monthDayTime(post.createdAt, isBattleRecord(post))}
                                      </span>
                                    )}
                                  </div>
                                  {/* 공덱 점수 — 전투기록 탭 줄과 같다(사용자 2026-10-26) */}
                                  <div style={{ width: 34, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                    <GradeBadge grade={post.simGrade} win={post.simWin} size={28} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })()
          ) : (
            initialPosts.map((post: any, idx: number) => {
              /** 방어 기록에는 상대 공덱이 없다 — 그게 방어 기록이라는 표시다 */
              const isDefenseLog = !post.attackTeam || post.attackTeam.length === 0;
              /** 방어 기록의 result 는 막아낸 사람 기준이라 뒤집지 않는다 */
              const defenseWon = isDefenseLog ? post.result === 'win' : post.result !== 'win';
              return (
            <div
              key={post._id}
              style={{
                background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)',
                borderRadius: idx === 0 ? '16px 16px 0 0' : idx === initialPosts.length - 1 ? '0 0 16px 16px' : 0,
                padding: '20px 20px 12px 20px',
                borderLeft: '2px solid rgba(102, 126, 234, 0.1)',
                borderRight: '2px solid rgba(102, 126, 234, 0.1)',
                borderTop: idx === 0 ? '2px solid rgba(102, 126, 234, 0.1)' : 'none',
                borderBottom: idx === initialPosts.length - 1 ? '2px solid rgba(102, 126, 234, 0.1)' : '1px solid rgba(102, 126, 234, 0.15)',
                display: 'flex',
                alignItems: 'center',
                minHeight: 60,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onClick={async () => {
                /**
                 * **팝업은 무조건 연다.**
                 *
                 * 예전에는 `setSelectedPost` 가 조회수 응답을 확인하는 `if` 안에 있어서,
                 * 조회수 올리기가 실패하면 팝업이 아예 열리지 않았다. 전투기록은 게시물이
                 * 아니라(`_id` 가 `siege-<logId>` 합성값) 그 요청이 늘 실패한다 —
                 * 그래서 전투기록 목록에서는 눌러도 아무 일도 없었다.
                 *
                 * 전투기록은 조회수를 셀 대상이 아니므로 요청 자체를 건너뛴다.
                 */
                const isSiegeRecord = typeof post._id === 'string' && post._id.startsWith('siege-');
                let newViews = post.views || 0;
                if (!isSiegeRecord) {
                  try {
                    const res = await fetch('/api/posts/view', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: post._id })
                    });
                    const data = await res.json();
                    if (data && typeof data.views === 'number') newViews = data.views;
                  } catch {}
                }
                setSelectedPost({ ...post, views: newViews });
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* 순번 */}
              <div style={{
                width: 80,
                minWidth: 80,
                maxWidth: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: 16,
                paddingRight: 16,
                borderRight: '1px solid rgba(102, 126, 234, 0.15)',
                fontSize: 13,
                fontWeight: 600,
                color: '#667eea'
              }}>
                {idx + 1}/{totalCount}
              </div>

              {/* 작성자 */}
              <div style={{ width: 180, minWidth: 180, maxWidth: 180, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 16, borderRight: '1px solid rgba(102, 126, 234, 0.15)' }}>
                <ProfileWithName
                  profileImg={post.profileImg || ''}
                  nickname={post.nickname || ''}
                  date={post.createdAt}
                  // 점령전 기록은 같은 분에 여러 건이 몰린다(실측: 580건이 서로 다른
                  // 초 575개에 흩어져 있었다). 그래서 이쪽만 초까지 보여준다
                  showSeconds={post.source === 'swex'}
                  color={post.isDeleted ? '#ff6b6b' : '#333333'}
                  size={48}
                  fontSize={14}
                  borderRadius={12}
                />
              </div>

              {/* 공덱과 방덱을 감싸는 컨테이너 (VS 포함) */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', gap: 16, borderRight: '1px solid rgba(102, 126, 234, 0.15)' }}>
                {/* 방어 기록은 상대 공덱이 없다(게임이 안 준다) — 그러면 공덱 칸도 VS 도 그리지 않는다 */}
                {!isDefenseLog && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {/* 몬스터들을 감싸는 테두리 박스 */}
                    <div style={{
                      position: 'relative',
                      border: post.result === 'win' ? '2px solid #2196F3' : '2px solid #f44336',
                      borderRadius: 12,
                      padding: '16px 12px 8px 12px',
                      background: post.result === 'win' ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                    }}>
                      {/* 공격팀 결과 라벨 - 테두리 상단에 위치 */}
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: post.result === 'win' ? '#2196F3' : '#f44336',
                        color: '#fff',
                        padding: '2px 12px',
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        boxShadow: post.result === 'win' ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 2px 4px rgba(244, 67, 54, 0.3)'
                      }}>
                        {post.result === 'win' ? 'WIN' : 'LOSE'}
                      </div>
                      {/* 몬스터들 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {post.attackTeam.map((m: any, idx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <div key={idx} style={{ position: 'relative' }}>
                              <img loading="lazy"
                                src={imgPath ? `/mobs/v2/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                alt={displayName}
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 12,
                                  objectFit: 'contain',
                                  background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #f8faff 100%)' : 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)',
                                  border: post.result === 'win' ? '2px solid rgba(33, 150, 243, 0.2)' : '2px solid rgba(244, 67, 54, 0.2)',
                                  boxShadow: post.result === 'win' ? '0 2px 8px rgba(33, 150, 243, 0.1)' : '0 2px 8px rgba(244, 67, 54, 0.1)'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {!isDefenseLog && post.defenseTeam?.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    padding: '8px 16px',
                    color: '#667eea',
                    fontWeight: 900,
                    fontSize: 20,
                    letterSpacing: '1.5px'
                  }}>
                    VS
                  </div>
                )}

                {/* 방어팀 — 방어 기록이면 이 칸만 보인다 */}
                {post.defenseTeam?.length > 0 && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {/* 몬스터들을 감싸는 테두리 박스 */}
                    <div style={{
                      position: 'relative',
                      border: defenseWon ? '2px solid #2196F3' : '2px solid #f44336',
                      borderRadius: 12,
                      padding: '16px 12px 8px 12px',
                      background: defenseWon ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                    }}>
                      {/* 방어팀 결과 라벨 - 테두리 상단에 위치 */}
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: defenseWon ? '#2196F3' : '#f44336',
                        color: '#fff',
                        padding: '2px 12px',
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        boxShadow: defenseWon ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 2px 4px rgba(244, 67, 54, 0.3)'
                      }}>
                        {defenseWon ? 'WIN' : 'LOSE'}
                      </div>
                      {/* 몬스터들 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {post.defenseTeam.map((m: any, idx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <div key={idx} style={{ position: 'relative' }}>
                              <img loading="lazy"
                                src={imgPath ? `/mobs/v2/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                alt={displayName}
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 12,
                                  objectFit: 'contain',
                                  background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)' : 'linear-gradient(135deg, #fff 0%, #f8faff 100%)',
                                  border: post.result === 'win' ? '2px solid rgba(244, 67, 54, 0.2)' : '2px solid rgba(33, 150, 243, 0.2)',
                                  boxShadow: post.result === 'win' ? '0 2px 8px rgba(244, 67, 54, 0.1)' : '0 2px 8px rgba(33, 150, 243, 0.1)'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 상대 길드 */}
              <div style={{ width: 180, minWidth: 180, maxWidth: 180, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 16 }}>
                {post.siegeWar ? (
                  <ProfileWithName
                    profileImg={guildImg(post.siegeWarImg || post.siegeWar)}
                    nickname={post.siegeWar}
                    color="#333333"
                    size={48}
                    fontSize={14}
                    borderRadius={12}
                    reverse={true}
                    alignRight={true}
                    defaultImg="/images/unknown2.png"
                  />
                ) : (
                  <span style={{ fontSize: 14, color: '#999' }}>-</span>
                )}
              </div>

              {/* 공덱 점수 — 점령전 공격 기록만(서버가 룬으로 시뮬레이션해 매긴다, 사용자 2026-10-26) */}
              {logType != null && (
              <div style={{ width: 56, minWidth: 56, maxWidth: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GradeBadge grade={post.simGrade} win={post.simWin} />
              </div>
              )}

              {/* 댓글 수 — 전투기록(logType 을 넘기는 SiegeBattleList)에는 댓글이 없어 안 그린다 (사용자 2026-10-22) */}
              {logType == null && (
              <div style={{ width: 80, minWidth: 80, maxWidth: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}><IconComment /></span>
                <span style={{ fontSize: 14, color: '#666', fontWeight: 500 }}>
                  {post.comments?.filter((c: any) => !c.isDeleted).length || 0}
                </span>
              </div>
              )}
            </div>
              );
          })
          )}
            </div>
        </div>

        {/* 무한 스크롤 트리거 & 로딩 인디케이터 */}
        {!isLoading || initialPosts.length > 0 ? (
        <div ref={observerTarget} style={{
          padding: '32px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 100
        }}>
          {isLoading && initialPosts.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              color: '#667eea',
              fontSize: 14,
              fontWeight: 600
            }}>
              <div style={{
                width: 24,
                height: 24,
                border: '3px solid rgba(102, 126, 234, 0.2)',
                borderTopColor: '#667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>로딩 중...</span>
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
        </div>
        ) : null}

        <CounterPost 
          open={!!selectedPost} 
          onClose={() => setSelectedPost(null)} 
          post={selectedPost} 
          monsterMapping={monsterMapping} 
          user={user} 
          onPostDeleted={onPostDeleted}
        />

        {/* 업로드 버튼 */}
        <button
          onClick={onUploadClick}
          style={{
            position: 'fixed',
            right: 40,
            bottom: 40,
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#1976d2',
            color: '#fff',
            fontSize: 38,
            fontWeight: 600,
            boxShadow: '0 4px 24px #1976d244',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1000,
            display: 'none'
          }}
          title="점령전 기록 업로드"
        >+</button>
        </>
        )}
      </div>
    </div>
  );
};

export default PostList; 
